import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {authGet, authPost, authPut, getAuthHeaders, hasToken, BACKEND_URL} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { toast, Toaster } from "sonner";
import { format, parseISO, differenceInDays } from "date-fns";
import { de } from "date-fns/locale";
import {
  ArrowLeft,
  LogOut,
  User,
  Mail,
  Calendar,
  Shield,
  Crown,
  Package,
  Clock,
  Download,
  MessageSquare,
  HelpCircle,
  Trash2,
  ChevronRight,
  ChevronDown,
  Send,
  AlertTriangle,
  Check,
  ExternalLink,
  Image,
  Video,
  Loader2,
  Camera,
  XCircle,
  AlertOctagon,
  CreditCard,
  BarChart3,
  Users,
  Image as ImageIcon,
  Sparkles,
  Zap,
  Lock,
  History,
  Settings
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import TransactionManagement from "@/components/admin/TransactionManagement";

const API = `${BACKEND_URL}/api`;

const THEME = {
  bg: "var(--brand-background)",
  bgSubtle: "var(--brand-text)",
  bgCard: "#FFFFFF",
  accent: "var(--brand-accent)",
  accentSubtle: "var(--brand-accent)22",
  text: "var(--brand-text)",
  textMuted: "var(--brand-text)60",
  fontSerif: "'Cormorant Garamond', serif",
  fontSans: "'Plus Jakarta Sans', sans-serif"
};

export default function Profile({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "account");
  const [profileData, setProfileData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // Profile picture state
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [profilePicture, setProfilePicture] = useState(user?.picture || null);

  // Contact form state
  const [contactSubject, setContactSubject] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactType, setContactType] = useState("bug");
  const [sendingContact, setSendingContact] = useState(false);

  // Cancel subscription state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // Delete account state
  const [showDangerZone, setShowDangerZone] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Media download state
  const [mediaInfo, setMediaInfo] = useState(null);

  // Usage tracking state
  const [usageData, setUsageData] = useState(null);
  const [limitsData, setLimitsData] = useState(null);

  useEffect(() => {
    if (!user) return;
    fetchProfileData();
    fetchMessages();
    fetchUnreadCount();
    fetchUsageData();
  }, [user]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && ["account", "messages", "support"].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const fetchProfileData = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`, {
        withCredentials: !hasToken(),
        headers: getAuthHeaders()
      });
      setProfileData(response.data);
      if (response.data?.user?.picture) {
        setProfilePicture(response.data.user.picture);
      }
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      // toast.error("Profil konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageData = async () => {
    try {
      const [usageRes, packageRes] = await Promise.all([
        axios.get(`${API}/user/usage`, {
          withCredentials: !hasToken(),
          headers: getAuthHeaders()
        }),
        axios.get(`${API}/user/package-info`, {
          withCredentials: !hasToken(),
          headers: getAuthHeaders()
        })
      ]);
      setUsageData(usageRes.data);
      setLimitsData(packageRes.data.limits);
    } catch (error) {
      console.error("Failed to fetch usage data:", error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/user/messages`, {
        withCredentials: !hasToken(),
        headers: getAuthHeaders()
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/user/messages/unread-count`, {
        withCredentials: !hasToken(),
        headers: getAuthHeaders()
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      // console.error("Failed to fetch unread count:", error);
    }
  };

  const markMessageRead = async (messageId) => {
    try {
      await axios.put(`${API}/user/messages/${messageId}/read`, {}, {
        withCredentials: !hasToken(),
        headers: getAuthHeaders()
      });
      setMessages(messages.map(m => m.id === messageId ? { ...m, is_read: true } : m));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error("Failed to mark message read:", error);
    }
  };

  const handleUploadPicture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bild darf maximal 5MB groß sein");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Nur Bilddateien erlaubt");
      return;
    }

    setUploadingPicture(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/user/profile/picture`, formData, {
        withCredentials: !hasToken(),
        headers: {
          "Content-Type": "multipart/form-data",
          ...getAuthHeaders()
        }
      });

      if (response.data.picture_url) {
        setProfilePicture(response.data.picture_url);
        toast.success("Profilbild aktualisiert!");
      }
    } catch (error) {
      console.error("Failed to upload picture:", error);
      toast.error("Profilbild konnte nicht hochgeladen werden");
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleSendContact = async () => {
    if (!contactSubject.trim() || !contactMessage.trim()) {
      toast.error("Bitte füllen Sie alle Felder aus");
      return;
    }

    setSendingContact(true);
    try {
      await axios.post(`${API}/user/contact`, {
        subject: contactSubject,
        message: contactMessage,
        contact_type: contactType
      }, {
        withCredentials: !hasToken(),
        headers: getAuthHeaders()
      });

      toast.success("Nachricht gesendet! Wir melden uns bald.");
      setContactSubject("");
      setContactMessage("");
      setContactType("bug");
    } catch (error) {
      console.error("Failed to send contact:", error);
      toast.error("Nachricht konnte nicht gesendet werden");
    } finally {
      setSendingContact(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/user/subscription/cancel`, {
        reason: cancelReason
      }, {
        withCredentials: !hasToken(),
        headers: getAuthHeaders()
      });

      toast.success("Abo wurde gekündigt. Es bleibt bis zum Ablaufdatum aktiv.");
      setShowCancelDialog(false);
      setCancelReason("");
      fetchProfileData();
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      toast.error(error.response?.data?.detail || "Kündigung fehlgeschlagen");
    } finally {
      setCancelling(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteEmail.toLowerCase() !== user?.email?.toLowerCase()) {
      toast.error("E-Mail stimmt nicht überein");
      return;
    }

    if (deleteConfirmText !== "LÖSCHEN") {
      toast.error("Bitte geben Sie 'LÖSCHEN' ein zur Bestätigung");
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/user/account`, {
        data: {
          confirm_email: deleteEmail,
          reason: deleteReason
        },
        withCredentials: !hasToken(),
        headers: getAuthHeaders()
      });

      toast.success("Account wurde gelöscht");
      setShowDeleteDialog(false);
      onLogout();
    } catch (error) {
      console.error("Failed to delete account:", error);
      toast.error(error.response?.data?.detail || "Account konnte nicht gelöscht werden");
    } finally {
      setDeleting(false);
    }
  };

  const fetchMediaInfo = async () => {
    try {
      const response = await axios.get(`${API}/user/media/download-all`, {
        withCredentials: !hasToken(),
        headers: getAuthHeaders()
      });
      setMediaInfo(response.data);
    } catch (error) {
      console.error("Failed to fetch media info:", error);
    }
  };

  const getPackageStatusColor = (status) => {
    switch (status) {
      case "active": return "text-emerald-400";
      case "expired": return "text-rose-400";
      case "cancelled": return "text-amber-400";
      default: return "text-white/40";
    }
  };

  const getPackageIcon = (tier) => {
    switch (tier) {
      case "premium": return <Crown className="w-5 h-5 text-amber-500" />;
      case "pro": return <Shield className="w-5 h-5 text-indigo-400" />;
      case "business": return <Package className="w-5 h-5 text-purple-400" />;
      default: return <Package className="w-5 h-5 text-white/40" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(parseISO(dateString), "dd. MMMM yyyy", { locale: de });
    } catch {
      return dateString;
    }
  };

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    try {
      const days = differenceInDays(parseISO(expiresAt), new Date());
      return days;
    } catch {
      return null;
    }
  };

  const GlobalBackground = () => (
    <div className="fixed inset-0 z-0 overflow-hidden bg-brand-background">
      <div className="absolute inset-0 bg-brand-background" />
      <div className="absolute inset-0 paper-texture opacity-30" />
    </div>
  );

  if (loading) return <div className="min-h-screen bg-brand-background flex items-center justify-center p-8"><div className="w-10 h-10 border-4 border-brand-accent border-t-transparent rounded-full animate-spin" /></div>;

  const packageInfo = profileData?.package;
  const stats = profileData?.statistics;
  const daysRemaining = packageInfo?.expires_at ? getDaysRemaining(packageInfo.expires_at) : null;
  const isSubscription = packageInfo?.details?.is_subscription;
  const canCancel = isSubscription && packageInfo?.status === "active" && !user?.is_admin;

  return (
    <div className="min-h-screen text-brand-text font-['Plus_Jakarta_Sans'] relative overflow-x-hidden antialiased pb-40">
      <GlobalBackground />
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="relative z-50 px-8 py-8 md:py-12 max-w-6xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-6">
          <button
            onClick={() => navigate(-1)}
            className="w-14 h-14 bg-white rounded-full border border-gray-100 flex items-center justify-center text-brand-text hover:bg-brand-accent hover:text-white transition-all shadow-xl group"
          >
            <ArrowLeft className="w-6 h-6 group-hover:scale-110 transition-transform" />
          </button>
          <div>
            <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: THEME.fontSans }}>Mein Profil</h1>
            <p className="text-[10px] font-black text-brand-accent uppercase tracking-[0.4em] mt-1">Sicherheit & Management</p>
          </div>
        </div>
        <button onClick={onLogout} className="flex items-center gap-2 text-rose-500 font-black uppercase text-[10px] tracking-widest hover:text-rose-700 transition-colors">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-8 py-4 space-y-12">
        <section className="bg-white rounded-[4rem] p-12 md:p-14 border border-gray-100 shadow-2xl flex flex-col items-center text-center gap-10 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-brand-accent/5 rounded-full blur-[100px] -mr-40 -mt-40 pointer-events-none group-hover:opacity-100 transition-opacity" />

          <div className="relative group/pic shrink-0">
            <div className="w-44 h-44 rounded-full border-8 border-white overflow-hidden bg-brand-background flex items-center justify-center shadow-2xl relative">
              {profilePicture ? (
                <img
                  src={profilePicture.startsWith("http") ? profilePicture : `${BACKEND_URL}${profilePicture}`}
                  className="w-full h-full object-cover scale-[1.01]"
                  alt="Profile"
                />
              ) : (
                <User className="w-20 h-20 text-gray-300" />
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-2 right-2 w-14 h-14 rounded-full bg-brand-accent text-white flex items-center justify-center shadow-2xl hover:bg-brand-text hover:scale-110 transition-all border-4 border-white z-10"
            >
              {uploadingPicture ? <Loader2 className="w-6 h-6 animate-spin" /> : <Camera className="w-6 h-6" />}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUploadPicture} className="hidden" />
          </div>

          <div className="flex-1 space-y-6 text-center md:text-left">
            <div>
              <h2 className="text-5xl font-black tracking-tighter text-brand-text">{user?.name || "Premium Creator"}</h2>
              <p className="text-lg font-bold text-gray-400 mt-2 flex items-center justify-center md:justify-start gap-3"><Mail className="w-5 h-5 text-brand-accent" /> {user?.email}</p>
            </div>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
              <span className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-2 border ${packageInfo?.status === "active" ? "bg-emerald-50 text-emerald-600 border-emerald-100" : "bg-gray-50 text-gray-400 border-gray-100"}`}>
                <Zap className="w-4 h-4" /> {packageInfo?.tier?.toUpperCase() || 'FREE'} {packageInfo?.status === "active" ? 'AKTIV' : 'ABGELAUFEN'}
              </span>
              <span className="px-5 py-2 bg-gray-50 border border-gray-100 rounded-full text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 flex items-center gap-2">
                <History className="w-4 h-4" /> Seit {formatDate(profileData?.user?.created_at)}
              </span>
            </div>
          </div>
        </section>

        {/* System Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-12">
          <TabsList className="w-full bg-white p-2 rounded-[2.5rem] border border-gray-100 h-auto grid grid-cols-3 shadow-xl">
            <TabsTrigger value="account" className="rounded-[2rem] py-5 data-[state=active]:bg-brand-text data-[state=active]:text-white data-[state=active]:shadow-xl text-[11px] font-black uppercase tracking-[0.3em] transition-all text-gray-400">
              <Lock className="w-4 h-4 mr-3" /> Account
            </TabsTrigger>
            <TabsTrigger value="messages" className="rounded-[2rem] py-5 data-[state=active]:bg-brand-text data-[state=active]:text-white data-[state=active]:shadow-xl text-[11px] font-black uppercase tracking-[0.3em] transition-all text-gray-400 relative">
              <MessageSquare className="w-4 h-4 mr-3" /> Postfach
              {unreadCount > 0 && <span className="absolute top-4 right-4 w-2.5 h-2.5 bg-brand-accent rounded-full shadow-[0_0_10px_var(--brand-accent)]" />}
            </TabsTrigger>
            <TabsTrigger value="support" className="rounded-[2rem] py-5 data-[state=active]:bg-brand-text data-[state=active]:text-white data-[state=active]:shadow-xl text-[11px] font-black uppercase tracking-[0.3em] transition-all text-gray-400">
              <HelpCircle className="w-4 h-4 mr-3" /> Support
            </TabsTrigger>
          </TabsList>

          <TabsContent value="account" className="space-y-10">
            {/* Subscriptions Card */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-[3.5rem] p-10 border border-gray-100 shadow-2xl space-y-10 relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-32 h-32 bg-brand-accent/5 blur-3xl rounded-full" />
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-black text-brand-text flex items-center gap-4">
                    <Zap className="w-8 h-8 text-brand-accent" /> Mein Plan
                  </h3>
                  <Badge className="bg-brand-accent text-white font-black">{packageInfo?.tier?.toUpperCase() || 'FREE'}</Badge>
                </div>
                <div className="space-y-6">
                  <div className="p-6 bg-brand-background rounded-3xl border border-gray-100 space-y-4">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Status & Laufzeit</p>
                    <div className="flex justify-between items-end">
                      <span className={`text-4xl font-black tracking-tighter ${getPackageStatusColor(packageInfo?.status)}`}>{packageInfo?.status?.toUpperCase() || 'NONE'}</span>
                      <span className="text-[11px] font-bold text-gray-400 mb-1">{packageInfo?.expires_at ? formatDate(packageInfo.expires_at) : 'Unbegrenzt'}</span>
                    </div>
                  </div>
                  {daysRemaining !== null && daysRemaining < 30 && (
                    <p className="text-xs font-bold text-amber-600 bg-amber-50 p-4 rounded-2xl flex items-center gap-3">
                      <AlertTriangle className="w-4 h-4" /> Dein Paket läuft in {daysRemaining} Tagen ab.
                    </p>
                  )}
                  <div className="flex gap-4">
                    <Button onClick={() => navigate("/pricing")} className="flex-1 rounded-2xl bg-brand-text text-white hover:bg-brand-accent hover:text-white font-black uppercase text-[10px] tracking-widest h-14">Plan Ändern</Button>
                    {canCancel && <Button onClick={() => setShowCancelDialog(true)} variant="ghost" className="flex-1 rounded-2xl bg-gray-50 text-rose-500 hover:bg-rose-500 hover:text-white font-black uppercase text-[10px] tracking-widest h-14 border border-gray-100">Kündigen</Button>}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-[3.5rem] p-10 border border-gray-100 shadow-2xl space-y-10">
                <h3 className="text-2xl font-black text-brand-text flex items-center gap-4">
                  <BarChart3 className="w-8 h-8 text-brand-accent" /> Platform Nutzung
                </h3>
                <div className="space-y-8">
                  {[
                    { label: 'Events Gestaltet', used: usageData?.events_count || 0, max: limitsData?.max_events === -1 ? '∞' : limitsData?.max_events, icon: Sparkles },
                    { label: 'Gäste Kapazität', used: usageData?.total_guests || 0, max: limitsData?.max_guests_per_event === -1 ? '∞' : limitsData?.max_guests_per_event, icon: Users },
                    { label: 'Media Cloud', used: usageData?.photos_count || 0, max: limitsData?.max_photos === -1 ? '∞' : limitsData?.max_photos, icon: ImageIcon }
                  ].map((st, i) => (
                    <div key={i} className="space-y-3">
                      <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                        <span className="text-gray-400 flex items-center gap-2"><st.icon className="w-3 h-3" /> {st.label}</span>
                        <span className="text-brand-text">{st.used} <span className="text-brand-accent">/ {st.max}</span></span>
                      </div>
                      <Progress value={st.max === '∞' ? 10 : (st.used / parseInt(st.max) * 100)} className="h-2 bg-gray-100 rounded-full overflow-hidden" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Statistics Strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: 'Events', val: stats?.events_created || 0, color: 'text-indigo-600' },
                { label: 'Gäste', val: stats?.total_guests || 0, color: 'text-emerald-600' },
                { label: 'Media Storage', val: `${stats?.storage_used_mb || 0}MB`, color: 'text-amber-600' },
                { label: 'Media Count', val: stats?.photos_count || 0, color: 'text-purple-600' }
              ].map((s, i) => (
                <div key={i} className="bg-white p-8 rounded-[2.5rem] border border-gray-100 text-center space-y-1 group hover:border-brand-accent/30 transition-all shadow-lg">
                  <p className={`text-4xl font-black tracking-tighter ${s.color} transition-transform group-hover:scale-110`}>{s.val}</p>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">{s.label}</p>
                </div>
              ))}
            </div>

            {/* System Management */}
            <div className="bg-white rounded-[3.5rem] p-10 border border-gray-100 shadow-2xl space-y-10">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-black text-brand-text flex items-center gap-4">
                  <Settings className="w-8 h-8 text-brand-accent" /> System Controls
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { fetchMediaInfo(); toast.info("Export wird vorbereitet..."); }}
                  className="rounded-full bg-brand-background text-brand-text font-black uppercase text-[10px] tracking-widest px-6 h-10 border border-gray-100 hover:bg-brand-accent hover:text-white"
                >
                  <Download className="w-4 h-4 mr-2" /> Alle Medien Exportieren
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 bg-brand-background rounded-3xl border border-gray-100 flex items-center gap-4">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center border border-gray-100 text-brand-accent"><Shield className="w-6 h-6" /></div>
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest italic">Authentifizierung</p>
                    <p className="text-lg font-black text-brand-text">{user?.auth_type === 'google' ? 'Google Account Bound' : 'Email & Password'}</p>
                  </div>
                </div>
                <div className="p-6 bg-brand-background rounded-3xl border border-gray-100 flex items-center justify-center gap-4 group cursor-pointer hover:bg-rose-50 transition-all" onClick={() => setShowDeleteDialog(true)}>
                  <Trash2 className="w-6 h-6 text-rose-500 group-hover:scale-110 transition-transform" />
                  <span className="text-[11px] font-black uppercase tracking-[0.3em] text-rose-500">Account dauerhaft löschen</span>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="messages" className="space-y-8">
            <div className="bg-white rounded-[3.5rem] p-12 border border-gray-100 shadow-2xl min-h-[50vh]">
              <div className="flex items-center gap-4 mb-10">
                <MessageSquare className="w-8 h-8 text-brand-accent" />
                <h3 className="text-2xl font-black text-brand-text">System Benachrichtigungen</h3>
              </div>
              <div className="space-y-6">
                {messages.length > 0 ? messages.map((msg, i) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
                    className={`p-8 rounded-[2.5rem] border ${msg.is_read ? 'bg-gray-50 border-gray-100 text-gray-400' : 'bg-white border-brand-accent text-brand-text shadow-xl'} group cursor-pointer transition-all hover:bg-brand-background`}
                    onClick={() => !msg.is_read && markMessageRead(msg.id)}
                  >
                    <div className="flex justify-between items-start gap-6">
                      <div className="space-y-3">
                        <h4 className={`text-xl font-black tracking-tight ${!msg.is_read && 'text-brand-accent'}`}>{msg.subject}</h4>
                        <p className="text-sm font-medium leading-relaxed italic">"{msg.message}"</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] font-black uppercase tracking-widest opacity-40">{formatDate(msg.created_at)}</p>
                        {!msg.is_read && <span className="inline-block mt-3 px-3 py-1 bg-brand-accent text-white text-[9px] font-black uppercase tracking-widest rounded-full">Neu</span>}
                      </div>
                    </div>
                  </motion.div>
                )) : (
                  <div className="h-[40vh] flex flex-col items-center justify-center text-gray-200">
                    <History className="w-20 h-20 mb-6 opacity-20" />
                    <p className="text-lg font-black uppercase tracking-[0.4em]">Keine Nachrichten</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="support" className="space-y-10">
            <div className="bg-white rounded-[3.5rem] p-12 border border-gray-100 shadow-2xl space-y-12">
              <div className="space-y-4">
                <h3 className="text-4xl font-black tracking-tighter text-brand-text">Support & Hilfe.</h3>
                <p className="text-gray-400 font-medium text-lg leading-relaxed max-w-xl">Hast du Fragen zu deinem Paket, technische Probleme oder Feedback für uns? Unser Team ist hier, um dir zu helfen.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                {[
                  { label: 'Ticketing', desc: 'Direkter Support', icon: Send },
                  { label: 'FAQ', desc: 'Schnelle Antworten', icon: HelpCircle },
                  { label: 'Policy', desc: 'Datenschutz & AGB', icon: Shield }
                ].map((it, i) => (
                  <div key={i} className="p-8 bg-brand-background rounded-[2.5rem] border border-gray-100 text-center space-y-4 hover:border-brand-accent/30 transition-all cursor-pointer group">
                    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mx-auto border border-gray-100 text-brand-accent group-hover:scale-110 transition-transform shadow-sm"><it.icon className="w-7 h-7" /></div>
                    <div>
                      <p className="font-black uppercase text-[11px] tracking-widest text-brand-text">{it.label}</p>
                      <p className="text-xs text-gray-400 font-bold mt-1">{it.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-10 border-t border-gray-100 pt-12">
                <h4 className="text-2xl font-black text-brand-text">Schreib uns eine Nachricht</h4>
                <div className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <Label className="text-[10px] font-black uppercase tracking-widest text-brand-accent">Betreff</Label>
                      <Input value={contactSubject} onChange={(e) => setContactSubject(e.target.value)} placeholder="Worum geht es?" className="bg-brand-background border-gray-100 rounded-2xl h-14" />
                    </div>
                    <div className="space-y-3">
                      <Label className="text-[10px] font-black uppercase tracking-widest text-brand-accent">Kategorie</Label>
                      <select value={contactType} onChange={(e) => setContactType(e.target.value)} className="w-full h-14 bg-brand-background border-gray-100 rounded-2xl px-4 text-sm font-bold focus:outline-none focus:ring-1 focus:ring-brand-accent text-brand-text">
                        <option value="bug">Technisches Problem</option>
                        <option value="billing">Abrechnung & Paket</option>
                        <option value="feedback">Feedback & Wünsche</option>
                        <option value="other">Sonstiges</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-brand-accent">Deine Nachricht</Label>
                    <Textarea value={contactMessage} onChange={(e) => setContactMessage(e.target.value)} placeholder="Beschreibe dein Anliegen..." className="bg-brand-background border-gray-100 rounded-[2.5rem] min-h-[200px] p-8" />
                  </div>
                  <button onClick={handleSendContact} disabled={sendingContact} className="bg-brand-text text-white hover:bg-brand-accent hover:text-white px-12 py-6 rounded-[2rem] font-black uppercase tracking-[0.3em] text-xs transition-all shadow-2xl flex items-center gap-3">
                    {sendingContact ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Nachricht Senden
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialogs */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="bg-white border-gray-100 text-brand-text rounded-[3rem] p-10 max-w-lg shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-3xl font-black tracking-tighter">Abo kündigen</DialogTitle>
            <DialogDescription className="text-gray-400 pt-4">Bist du sicher, dass du dein {packageInfo?.tier}-Abo kündigen möchtest? Du behältst alle Vorteile bis zum Ende des aktuellen Zeitraums.</DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <Label className="text-[10px] font-black uppercase tracking-widest text-brand-accent mb-2 block">Grund der Kündigung (optional)</Label>
            <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Erzähle uns kurz, warum du gehst..." className="bg-brand-background border-gray-100 rounded-2xl" />
          </div>
          <DialogFooter className="gap-4">
            <Button variant="ghost" onClick={() => setShowCancelDialog(false)} className="rounded-2xl h-14 font-black uppercase tracking-widest text-xs">Abbrechen</Button>
            <Button onClick={handleCancelSubscription} disabled={cancelling} className="bg-rose-500 hover:bg-rose-600 text-white rounded-2xl h-14 px-8 font-black uppercase tracking-widest text-xs">
              {cancelling ? <Loader2 className="w-5 h-5 animate-spin" /> : "Jetzt Kündigen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white border-rose-500/20 text-brand-text rounded-[3rem] p-10 max-w-lg shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-3xl font-black tracking-tighter text-rose-500">Account Löschen</DialogTitle>
            <DialogDescription className="text-gray-400 pt-4">Dies ist eine endgültige Aktion. Alle deine Events, Gästelisten und Medien werden sofort und unwiderruflich gelöscht.</DialogDescription>
          </DialogHeader>
          <div className="py-6 space-y-6">
            <div className="space-y-3">
              <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Bestätige deine Email ({user?.email})</Label>
              <Input value={deleteEmail} onChange={(e) => setDeleteEmail(e.target.value)} className="bg-brand-background border-gray-100 rounded-2xl" />
            </div>
            <div className="space-y-3">
              <Label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Tippe "LÖSCHEN" zur Bestätigung</Label>
              <Input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} className="bg-brand-background border-gray-100 rounded-2xl" />
            </div>
          </div>
          <DialogFooter className="gap-4">
            <Button variant="ghost" onClick={() => setShowDeleteDialog(false)} className="rounded-2xl h-14 font-black uppercase tracking-widest text-xs">Zurück</Button>
            <Button onClick={handleDeleteAccount} disabled={deleting} className="bg-rose-600 hover:bg-rose-700 text-white rounded-2xl h-14 px-8 font-black uppercase tracking-widest text-xs">
              {deleting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Unwiderruflich Löschen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
